import {
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  OnInit,
  signal,
  viewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { catchError, debounceTime, filter, of, switchMap } from 'rxjs';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import {
  AlertComponent,
  ButtonComponent,
  TextFieldWithErrorComponent
} from 'htx-ui-library';
import { ProtectedContentComponent } from '../../../../organisms/protected-content/protected-content.component';
import { HtmlCodeEditorComponent } from '../../../../organisms/html-code-editor/html-code-editor.component';
import {
  ConfirmationDialogComponent,
  ConfirmationDialogData
} from '../../../../organisms/confirmation-dialog/confirmation-dialog.component';
import { NavigationService } from '../../../../../services/navigation.service';
import { NotificationService } from '../../../../../services/form/notification.service';
import { EmailTemplateService } from '../../../../../services/email-templates/email-template.service';
import {
  EmailTemplateDetail,
  EmailTemplateKey,
  EmailTemplateUpdateDto
} from '../../../../../services/email-templates/email-template.model';

@Component({
  selector: 'app-email-template-edit-page',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslocoDirective,
    ButtonComponent,
    AlertComponent,
    TextFieldWithErrorComponent,
    HtmlCodeEditorComponent,
    ProtectedContentComponent
  ],
  templateUrl: './email-template-edit-page.component.html',
  styleUrl: './email-template-edit-page.component.scss'
})
export class EmailTemplateEditPageComponent implements OnInit {
  private fb = inject(FormBuilder);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private dialog = inject(MatDialog);
  private emailTemplateService = inject(EmailTemplateService);
  private notificationService = inject(NotificationService);
  private translocoService = inject(TranslocoService);
  private navigationService = inject(NavigationService);
  private destroyRef = inject(DestroyRef);

  requiredRoles = this.navigationService.getAccessRoles('admin/email-templates');

  private key!: EmailTemplateKey;

  detail = signal<EmailTemplateDetail | null>(null);
  saving = signal<boolean>(false);

  previewSubject = signal<string>('');
  previewBodyHtml = signal<string>('');
  previewError = signal<string | null>(null);

  private previewFrameRef = viewChild<ElementRef<HTMLIFrameElement>>('previewFrame');

  templateName = computed<string>(() => {
    const key = this.detail()?.key;
    return key ? this.translocoService.translate(`dsaManagement.emailTemplates.templates.${key}.name`) : '';
  });

  form = this.fb.group({
    subject: ['', [Validators.required, Validators.maxLength(255)]],
    bodyHtml: ['', [Validators.required]]
  });

  constructor() {
    // Push the rendered preview HTML into the iframe imperatively. This avoids
    // Angular's HTML sanitization of the `srcdoc` binding (which would mangle a
    // full HTML document); the iframe's `sandbox` without `allow-scripts`
    // contains any embedded script.
    effect(() => {
      const frame = this.previewFrameRef()?.nativeElement;
      if (frame) {
        frame.srcdoc = this.previewBodyHtml();
      }
    });
  }

  ngOnInit(): void {
    this.key = this.route.snapshot.paramMap.get('key') as EmailTemplateKey;

    this.emailTemplateService
      .get(this.key)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (detail) => {
          this.applyDetail(detail);
        },
        error: () => {
          this.notificationService.showError('dsaManagement.emailTemplates.edit.loadError', 5000);
          void this.router.navigate(['admin', 'email-templates']);
        }
      });

    // Debounced live preview on every form change.
    this.form.valueChanges
      .pipe(
        debounceTime(400),
        switchMap(() => this.requestPreview()),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((result) => this.applyPreview(result));
  }

  private requestPreview() {
    const dto = this.currentDto();
    return this.emailTemplateService.preview(this.key, dto).pipe(
      catchError((err) => {
        const message =
          err?.error?.message ??
          this.translocoService.translate('dsaManagement.emailTemplates.edit.preview.error');
        this.previewError.set(message);
        return of(null);
      })
    );
  }

  private refreshPreview(): void {
    this.requestPreview().subscribe((result) => this.applyPreview(result));
  }

  private applyPreview(result: { subject: string; bodyHtml: string } | null): void {
    if (!result) {
      return;
    }
    this.previewError.set(null);
    this.previewSubject.set(result.subject);
    this.previewBodyHtml.set(result.bodyHtml);
  }

  private applyDetail(detail: EmailTemplateDetail & { htmlBody?: string }): void {
    const subject = detail.subject ?? detail.defaultSubject ?? '';
    const bodyHtml = detail.bodyHtml || detail.htmlBody || detail.defaultBodyHtml || '';

    this.detail.set({
      key: detail.key,
      subject,
      defaultSubject: detail.defaultSubject,
      bodyHtml,
      defaultBodyHtml: detail.defaultBodyHtml,
      overridden: detail.overridden
    });

    this.form.setValue(
      {
        subject,
        bodyHtml
      },
      { emitEvent: false }
    );
    this.form.markAsPristine();
    this.form.markAsUntouched();
    this.refreshPreview();
  }

  private currentDto(): EmailTemplateUpdateDto {
    const value = this.form.getRawValue();
    return {
      subject: value.subject ?? '',
      bodyHtml: value.bodyHtml ?? ''
    };
  }

  save(): void {
    if (this.form.invalid || this.saving()) {
      return;
    }
    this.saving.set(true);
    this.emailTemplateService.update(this.key, this.currentDto()).subscribe({
      next: () => {
        this.saving.set(false);
        this.notificationService.showSuccess(
          'dsaManagement.emailTemplates.edit.notifications.saved',
          5000
        );
        void this.router.navigate(['admin', 'email-templates']);
      },
      error: () => {
        this.saving.set(false);
        this.notificationService.showError(
          'dsaManagement.emailTemplates.edit.notifications.saveError',
          5000
        );
      }
    });
  }

  reset(): void {
    const data: ConfirmationDialogData = {
      title: this.translocoService.translate('dsaManagement.emailTemplates.edit.resetConfirm.title'),
      message: this.translocoService.translate(
        'dsaManagement.emailTemplates.edit.resetConfirm.message'
      ),
      confirmText: this.translocoService.translate(
        'dsaManagement.emailTemplates.edit.resetConfirm.confirm'
      ),
      cancelText: this.translocoService.translate(
        'dsaManagement.emailTemplates.edit.resetConfirm.cancel'
      ),
      variant: 'warning',
      iconVariant: 'warning',
      icon: 'restart_alt'
    };

    this.dialog
      .open(ConfirmationDialogComponent, { data, width: '480px', autoFocus: false })
      .afterClosed()
      .pipe(filter((confirmed) => confirmed === true))
      .subscribe(() => {
        this.emailTemplateService.reset(this.key).subscribe({
          next: () => {
            this.notificationService.showSuccess(
              'dsaManagement.emailTemplates.edit.notifications.resetDone',
              5000
            );
            void this.router.navigate(['admin', 'email-templates']);
          },
          error: () =>
            this.notificationService.showError(
              'dsaManagement.emailTemplates.edit.notifications.saveError',
              5000
            )
        });
      });
  }

  cancel(): void {
    void this.router.navigate(['admin', 'email-templates']);
  }
}
